/**
 * Custom hook to convert a given price to a specified currency.
 *
 * @param price - The original price to be converted.
 * @param to - The target currency code to convert the price to. If not provided, the user's preferred currency will be used.
 * @param from - The source currency code of the original price. If not provided, the default currency will be used.
 * @returns An object containing the converted price and metadata about the target currency.
 * @property price - The converted price.
 * @property meta - Metadata about the target currency.
 * @property meta.code - The currency code of the target currency.
 * @property meta.symbol - The symbol of the target currency.
 * @property meta.name - The name of the target currency.
 */
"use client";

import { useExchangeRates } from "@/providers/exchange-rate-provider";
import { useUserCurrency } from "@/providers/user-preferences-provider";

export function useConvertedPrice(price: number, to?: string, from?: string) {
  const exchangeRates = useExchangeRates();
  const userCurrency = useUserCurrency();
  const code = to ?? userCurrency;
  const convertedPrice = exchangeRates.convert(price, code, from);
  const currency = exchangeRates.getCurrency(code);
  return {
    price: convertedPrice,
    meta: {
      ...currency,
      code,
    },
  } as const;
}
